import { useState, type FormEvent } from 'react';
import { Dialog } from '@/components/dialog';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { useToast } from '@/components/toast';
import { useFormError } from '@/lib/form-error';
import { formatPhone } from '@/lib/format';
import { useClients } from '@/lib/clients';
import { useIdentifyNumber, type IdentifyAction } from '@/lib/contacts-directory';

const ACTIONS: { value: IdentifyAction; label: string }[] = [
  { value: 'contact', label: 'Save as a contact (vendor, personal, other)' },
  { value: 'client_contact', label: 'Add to an existing client' },
  { value: 'lead', label: 'Start a new lead' },
  { value: 'client', label: 'Create a new client' },
  { value: 'block', label: 'Block this number' },
];

/**
 * Turn an unknown phone number into someone the CRM understands. Shown from a
 * Messages thread or a Calls row when the party isn't linked yet.
 */
export function IdentifyDialog({
  number,
  open,
  onClose,
}: {
  number: string;
  open: boolean;
  onClose: () => void;
}) {
  const identify = useIdentifyNumber();
  const toast = useToast();
  const { error, wrap } = useFormError();
  const { data: clientsPage } = useClients();
  const clients = clientsPage?.data ?? [];

  const [action, setAction] = useState<IdentifyAction>('contact');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'vendor' | 'personal' | 'other'>('vendor');
  const [clientId, setClientId] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState('');

  const resolvedClientId = clientId ?? clients[0]?.id ?? null;

  function reset() {
    setAction('contact');
    setName('');
    setKind('vendor');
    setClientId(null);
    setCompanyName('');
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const ok = await wrap(() =>
      identify.mutateAsync({
        number,
        action,
        name: name.trim() || undefined,
        kind: action === 'contact' ? kind : undefined,
        client_id: action === 'client_contact' ? (resolvedClientId ?? undefined) : undefined,
        company_name:
          action === 'lead' || action === 'client' ? companyName.trim() || undefined : undefined,
      }),
    );
    if (ok) {
      const done: Record<IdentifyAction, string> = {
        contact: 'Contact saved',
        client_contact: 'Added to the client',
        lead: 'Lead created',
        client: 'Client created',
        block: 'Number blocked',
      };
      toast.success(done[action]);
      reset();
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={`Identify ${formatPhone(number)}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="What is this number?">
          <Select value={action} onChange={(e) => setAction(e.target.value as IdentifyAction)}>
            {ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Select>
        </Field>

        {action !== 'block' ? (
          <Field label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Dana Reyes"
              autoFocus
            />
          </Field>
        ) : null}

        {action === 'contact' ? (
          <Field label="Type">
            <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="vendor">Vendor</option>
              <option value="personal">Personal</option>
              <option value="other">Other</option>
            </Select>
          </Field>
        ) : null}

        {action === 'client_contact' ? (
          <Field label="Client">
            {clients.length === 0 ? (
              <p className="text-sm text-[var(--color-foreground-muted)]">
                No clients yet. Create one first, or choose another option.
              </p>
            ) : (
              <Select
                value={resolvedClientId ?? ''}
                onChange={(e) => setClientId(Number(e.target.value))}
              >
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : null}

        {action === 'lead' || action === 'client' ? (
          <Field label={action === 'client' ? 'Company name (optional)' : 'Company (optional)'}>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="e.g. Acme Co."
            />
          </Field>
        ) : null}

        {action === 'block' ? (
          <p className="text-sm text-[var(--color-foreground-muted)]">
            Blocked numbers stay recorded but are hidden from the inbox. You can unblock a number
            later from the Contacts page.
          </p>
        ) : null}

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={action === 'block' ? 'danger' : 'primary'}
            disabled={
              identify.isPending || (action === 'client_contact' && resolvedClientId === null)
            }
          >
            {identify.isPending ? 'Saving…' : action === 'block' ? 'Block number' : 'Save'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}
